import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Beaker, AlertTriangle, Plus, Loader2, CheckCircle2, Filter, Search,
} from "lucide-react";
import { ACCENT, fmtDate, Field, HistoryTable } from "./shared";

const GRADE_COLOR = {
  1: "#4ADE80",  // Mild
  2: "#FBBF24",  // Moderate
  3: "#FB923C",  // Severe
  4: "#F87171",  // Life-threatening
  5: "#DC2626",  // Death
};
const GRADE_LABEL = {
  1: "Mild", 2: "Moderate", 3: "Severe",
  4: "Life-threatening", 5: "Death",
};

/**
 * CTCAE v5.0 Toxicity Tracker.
 * Backend: GET /toxicities/reference · POST /toxicities · GET /toxicities.
 * Grade ≥ 3 events automatically insert into `ai_cross_insights` so they
 * appear as red-badged alerts in the Provider Inbox.
 */
export default function ToxicityTab({ data, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [socFilter, setSocFilter] = useState("");
  const [minGradeFilter, setMinGradeFilter] = useState(0);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    term: "", grade: 1, attribution: "possible",
    action_taken: "supportive_only", notes: "",
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/specialties/oncology/toxicities/reference");
        setCatalog(r);
      } catch (err) {
        toast.error("Failed to load CTCAE catalog");
      }
    })();
  }, []);

  const filteredItems = useMemo(() => {
    if (!catalog?.items) return [];
    const needle = q.trim().toLowerCase();
    return catalog.items.filter((it) => {
      if (socFilter && it.soc !== socFilter) return false;
      if (needle && !it.term.toLowerCase().includes(needle) &&
          !it.meddra_code.includes(needle)) return false;
      return true;
    });
  }, [catalog, q, socFilter]);

  function pickItem(item) {
    setSelectedItem(item);
    setForm({ ...form, term: item.term });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.term) {
      toast.error("Choose a CTCAE term first");
      return;
    }
    setSaving(true);
    try {
      await api.post("/specialties/oncology/toxicities", {
        date: form.date,
        term: form.term,
        grade: Number(form.grade),
        attribution: form.attribution,
        action_taken: form.action_taken,
        notes: form.notes || null,
      });
      toast.success(`${form.term} G${form.grade} recorded${form.grade >= 3 ? " · provider alert queued" : ""}`);
      setForm({ ...form, notes: "" });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record toxicity");
    } finally { setSaving(false); }
  }

  const rows = (data?.toxicities || []).filter((r) => r.grade >= minGradeFilter);
  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="onco-toxicity-tab">
      {/* Header summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label="Total events" value={summary.total || 0} color="#fff" testId="tox-summary-total" />
        <SummaryTile label="Unresolved" value={summary.unresolved || 0} color="#FBBF24" testId="tox-summary-unresolved" />
        <SummaryTile label="Grade ≥ 3" value={summary.grade_3_plus_count || 0}
                     color={summary.grade_3_plus_count ? "#F87171" : "#4ADE80"} testId="tox-summary-severe" />
        <SummaryTile label="Max grade seen" value={summary.max_grade ? `G${summary.max_grade}` : "—"}
                     color={GRADE_COLOR[summary.max_grade] || "#A3ACA7"} testId="tox-summary-max" />
      </div>

      {/* Record form */}
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="tox-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-lg">Record CTCAE v5.0 toxicity</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Full 26-SOC curated catalog. Grade ≥ 3 auto-alerts the provider inbox.
            </div>
          </div>
          {catalog && (
            <div className="text-[10px] uppercase tracking-wider text-[#7A8480]">
              {catalog.version} · {catalog.items?.length || 0} items
            </div>
          )}
        </div>

        {/* Search / SOC filter */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Field label="Search term / MedDRA code">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3ACA7]" />
                <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                       data-testid="tox-search"
                       placeholder="e.g. neutropenia · 10029366 · nausea"
                       className="ros-input pl-9 w-full" />
              </div>
            </Field>
          </div>
          <Field label="System organ class">
            <select value={socFilter} onChange={(e) => setSocFilter(e.target.value)}
                    data-testid="tox-soc-filter" className="ros-input">
              <option value="">All SOC ({(catalog?.soc || []).length})</option>
              {(catalog?.soc || []).map((soc) => (
                <option key={soc} value={soc}>{soc}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* AE picker (scrollable) */}
        <div className="border border-[#272E2B] rounded-2xl max-h-56 overflow-y-auto"
             data-testid="tox-picker">
          {filteredItems.length === 0 ? (
            <div className="p-4 text-sm text-[#A3ACA7]">No matching AEs.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {filteredItems.map((it) => (
                  <tr key={it.meddra_code}
                      className={`border-t border-[#272E2B] cursor-pointer transition-colors ${
                        selectedItem?.meddra_code === it.meddra_code
                          ? "bg-[#FBBF24]/10" : "hover:bg-[#1C201E]"
                      }`}
                      onClick={() => pickItem(it)}
                      data-testid={`tox-item-${it.meddra_code}`}>
                    <td className="py-2 px-3 text-white">{it.term}</td>
                    <td className="py-2 px-3 text-[10px] text-[#7A8480] font-mono">{it.meddra_code}</td>
                    <td className="py-2 px-3 text-[10px] text-[#A3ACA7]">{it.soc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedItem && (
          <div className="ros-card p-4 space-y-3" style={{ background: "#0F1312" }}
               data-testid="tox-grade-criteria">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Selected</div>
                <div className="font-heading text-base">{selectedItem.term}</div>
                <div className="text-[10px] text-[#7A8480]">{selectedItem.soc} · MedDRA {selectedItem.meddra_code}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Grade</div>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((g) => {
                    const active = Number(form.grade) === g;
                    const disabled = !selectedItem.grades[String(g)] ||
                                     selectedItem.grades[String(g)] === "—";
                    return (
                      <button type="button" key={g}
                              onClick={() => !disabled && setForm({ ...form, grade: g })}
                              disabled={disabled}
                              data-testid={`tox-grade-btn-${g}`}
                              className={`w-9 h-9 rounded-lg font-bold text-sm transition-colors ${
                                disabled ? "opacity-30 cursor-not-allowed bg-[#1C201E]" : ""
                              }`}
                              style={{
                                background: active ? GRADE_COLOR[g] : "#1C201E",
                                color: active ? "#000" : "#fff",
                                border: `1px solid ${active ? GRADE_COLOR[g] : "#272E2B"}`,
                              }}>
                        G{g}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="pt-2 border-t border-[#272E2B]">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
                Grade {form.grade} criteria — {GRADE_LABEL[form.grade]}
              </div>
              <div className="text-sm text-white mt-1 leading-relaxed">
                {selectedItem.grades[String(form.grade)] || "—"}
              </div>
              {form.grade >= 3 && (
                <div className="mt-2 text-[11px] font-semibold flex items-center gap-1.5"
                     style={{ color: GRADE_COLOR[form.grade] }}
                     data-testid="tox-alert-badge">
                  <AlertTriangle size={12} /> Will auto-alert provider inbox
                  ({form.grade >= 4 ? "critical" : "high"} priority)
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Onset date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="tox-date" className="ros-input" required />
          </Field>
          <Field label="Attribution">
            <select value={form.attribution}
                    onChange={(e) => setForm({ ...form, attribution: e.target.value })}
                    data-testid="tox-attribution" className="ros-input">
              <option value="unrelated">Unrelated</option>
              <option value="unlikely">Unlikely</option>
              <option value="possible">Possible</option>
              <option value="probable">Probable</option>
              <option value="definite">Definite</option>
            </select>
          </Field>
          <Field label="Action taken">
            <select value={form.action_taken}
                    onChange={(e) => setForm({ ...form, action_taken: e.target.value })}
                    data-testid="tox-action" className="ros-input">
              <option value="supportive_only">Supportive only</option>
              <option value="dose_delay">Dose delay</option>
              <option value="dose_reduce">Dose reduce</option>
              <option value="dose_hold">Dose hold</option>
              <option value="discontinue">Discontinue therapy</option>
              <option value="hospitalise">Hospitalise</option>
            </select>
          </Field>
        </div>
        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="tox-notes" className="ros-input"
                    placeholder="Timing, workup, response to intervention…" />
        </Field>

        <div>
          <button type="submit" disabled={saving || !form.term}
                  data-testid="tox-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Recording…" : "Record toxicity"}
          </button>
        </div>
      </form>

      {/* History with grade filter */}
      <div className="flex items-center gap-3">
        <Filter size={14} className="text-[#A3ACA7]" />
        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Filter by grade ≥</div>
        {[0, 1, 2, 3, 4, 5].map((g) => (
          <button key={g} onClick={() => setMinGradeFilter(g)}
                  data-testid={`tox-history-filter-${g}`}
                  className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                    minGradeFilter === g
                      ? "bg-[#FBBF24] text-black"
                      : "bg-[#1C201E] text-[#A3ACA7] hover:text-white"
                  }`}>
            {g === 0 ? "All" : `G${g}+`}
          </button>
        ))}
      </div>

      <HistoryTable
        title={`Toxicity history (${rows.length})`}
        testId="tox-history"
        rows={rows}
        rowTestId={(r) => `tox-row-${r.id}`}
        empty="No CTCAE toxicities recorded yet."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Term", render: (r) => (
            <span>
              <div className="text-white">{r.term_normalised}</div>
              <div className="text-[10px] text-[#7A8480]">{r.soc}</div>
            </span>
          )},
          { label: "Grade", render: (r) => (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{
                    color: GRADE_COLOR[r.grade],
                    background: `${GRADE_COLOR[r.grade]}18`,
                    border: `1px solid ${GRADE_COLOR[r.grade]}40`,
                  }}>
              G{r.grade} · {r.grade_label}
            </span>
          )},
          { label: "Attribution", render: (r) => (
            <span className="text-xs text-[#A3ACA7] capitalize">{r.attribution || "—"}</span>
          )},
          { label: "Action", render: (r) => (
            <span className="text-xs text-[#A3ACA7]">
              {(r.action_taken || "—").replace(/_/g, " ")}
            </span>
          )},
          { label: "Status", render: (r) => (
            r.resolved
              ? <span className="text-xs text-[#4ADE80] flex items-center gap-1"><CheckCircle2 size={12} /> Resolved</span>
              : <span className="text-xs text-[#FBBF24]">Active</span>
          )},
        ]}
      />
    </div>
  );
}

function SummaryTile({ label, value, color, testId }) {
  return (
    <div className="ros-card" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</div>
      <div className="text-3xl font-heading font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
